import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Export,
  InternationalMarket,
  PropertyKey,
  SivaPhase,
  SustainabilityReadinessLevel,
  TargetNumberValueExtendedDto,
} from 'src/api/v2';
import Integer, { lowercaseFirstLetter } from 'src/features/siva/reporting/components/EditableCells/Integer';

import { Button } from '@innovationnorway/cl-react';
import { PageParams } from '../company-details/types/PageParams';
import useCompanyContract from '../company-details/hooks/use-company-contract';
import { convertIdToNumber } from '../company-details/utils/idConversion';
import useCompanyTargetNumbers from '../company-details/hooks/use-company-target-numbers';
import { getContractCurrentPhase } from '../company-details/utils/helpers';
import DropdownEnum from '../../reporting/components/EditableCells/DropdownEnum';
import { capitalizeFirstLetter } from 'src/utils/string';
import { formatDateToNOString } from 'src/utils/FormatValue';
import BoolCell from '../../reporting/components/EditableCells/BoolCell';
import EuEnviromentalGoals from '../../reporting/components/EditableCells/EuEnviromentalGoals';
import ValueField from 'src/components/ValueField/ValueField';
import { useOpenReportingYears } from '../hooks/use-siva-open-reporting-years';
import { useCluster } from 'src/features/cluster';
import { useTranslation } from 'react-i18next';
import { resources } from 'src/i18n';
import { transformToSrlReference } from 'src/features/siva/ideas-and-companies/utils';
import { internationalMarketTranslationSet } from '../constants';

type Props = {
  year: number;
};

const unreachableCase = (_type: never) => <></>;

const GoalAchievementYear = ({ year }: Props) => {
  const { contractId } = useParams<PageParams>();
  const { contract } = useCompanyContract(convertIdToNumber(contractId));
  const { t } = useTranslation('targetNumbers');
  const { targetNumbers, updatedAt } = useCompanyTargetNumbers(contractId, year);

  const { cluster } = useCluster();
  const { openReportingYears } = useOpenReportingYears(cluster?.id);

  const { t: tSiva } = useTranslation('siva');

  const savedAt = useMemo(() => {
    return new Date(updatedAt);
  }, [updatedAt]);

  const sortedData = useMemo(
    () =>
      targetNumbers?.sort((a, b) =>
        (a.goalCategory.name as string).localeCompare(b.goalCategory.name as string),
      ),
    [targetNumbers],
  );

  const [editMode, setEditMode] = useState(false);

  const editable = useMemo(
    () => editMode && openReportingYears.includes(year),
    [editMode, openReportingYears, year],
  );

  const renderInput = (targetNumber: TargetNumberValueExtendedDto) => {
    if (!contract) return <></>;

    const targetNumberObject = {
      contractName: contract.name,
      currentContractPhase: getContractCurrentPhase(contract.phases)?.sivaPhase || SivaPhase.Incubated,
      goalTemplateId: targetNumber.goalTemplateId || '',
      sivaContractId: Number(contractId),
      valueDto: targetNumber.targetNumberValueDto,
      year: year,
    };

    if (!targetNumber.targetNumberValueDto && !editMode) return <div>--</div>;

    const getExportText = (exp: Export) => {
      switch (exp) {
        case Export.Potensiale:
          return tSiva('report.goals.export.potensiale');
        case Export.Underleverandor:
          return tSiva('report.goals.export.underleverandor');
        default:
          return exp;
      }
    };

    switch (targetNumber.propertyKey) {
      case PropertyKey.Equity:
        return (
          <Integer
            propertyKey={targetNumber.propertyKey}
            editMode={editable}
            targetNumber={targetNumberObject}
          />
        );

      case PropertyKey.Srl:
        return (
          <DropdownEnum
            targetNumber={targetNumberObject}
            propertyKey={targetNumber.propertyKey}
            editMode={editable}
            options={Object.values(SustainabilityReadinessLevel).map((srl) => ({
              text: transformToSrlReference(srl),
              value: srl,
            }))}
          />
        );

      case PropertyKey.AdmissionDate:
        return targetNumber.targetNumberValueDto &&
          targetNumber.targetNumberValueDto[lowercaseFirstLetter(targetNumber.propertyKey)]
          ? capitalizeFirstLetter(
              formatDateToNOString(
                targetNumber.targetNumberValueDto[lowercaseFirstLetter(targetNumber.propertyKey)],
              ),
            )
          : '';

      case PropertyKey.BusinessModelDigitalization:
        return (
          <BoolCell
            targetNumber={targetNumberObject}
            objectKey={targetNumber.propertyKey}
            editMode={editable}
          />
        );
      case PropertyKey.BusinessModelExport:
        return (
          <DropdownEnum
            targetNumber={targetNumberObject}
            propertyKey={targetNumber.propertyKey}
            editMode={editable}
            options={Object.keys(Export).map((exp) => ({
              text: getExportText(Export[exp]),
              value: exp,
            }))}
          />
        );

      case PropertyKey.EsaSummary:
        //Tror ikke denne kan dukke opp engang, men legger til en case håndtering uansett

        return <>ESA tall kan ikke redigeres her, gå til siden for ESA-rapportering i menyen til venstre</>;

      case PropertyKey.EuEnvironmentalGoals:
        return (
          <EuEnviromentalGoals
            targetNumber={targetNumberObject}
            objectKey={targetNumber.propertyKey}
            editMode={editable}
          />
        );

      case PropertyKey.InternationalMarket:
        return (
          <DropdownEnum
            targetNumber={targetNumberObject}
            propertyKey={targetNumber.propertyKey}
            editMode={editable}
            options={Object.values(InternationalMarket).map((im) => ({
              text: tSiva(internationalMarketTranslationSet[im]),
              value: im,
            }))}
          />
        );

      case PropertyKey.WomenInLeadingRole:
        return (
          <BoolCell
            targetNumber={targetNumberObject}
            objectKey={targetNumber.propertyKey}
            editMode={editable}
          />
        );

      case PropertyKey.BusinessModelSustainability:
        return (
          <BoolCell
            targetNumber={targetNumberObject}
            objectKey={targetNumber.propertyKey}
            editMode={editable}
          />
        );

      case PropertyKey.AdvisedAccessToOtherTools:
        return (
          <BoolCell
            targetNumber={targetNumberObject}
            objectKey={targetNumber.propertyKey}
            editMode={editable}
          />
        );
      case PropertyKey.InternationalCooperation:
        return (
          <BoolCell
            targetNumber={targetNumberObject}
            objectKey={targetNumber.propertyKey}
            editMode={editable}
          />
        );
      default:
        //Om vi utvider enum for måloppnåelse, så vil den klikke om nytt case ikke er håndtert.
        //På denne måten får vi beskjed om vi mangler noe.
        return unreachableCase(targetNumber.propertyKey);
    }
  };

  return (
    <>
      <h2>{tSiva('ideasAndCompanies.resultsMetrics.header')}</h2>
      <div className="margin-top--auto margin-bottom--auto font-size--large">
        {tSiva('ideasAndCompanies.changesAreAutomatic.text')} {savedAt.toLocaleTimeString('nb')}
      </div>
      {!openReportingYears.includes(year) && <p>{tSiva('ideasAndCompanies.reportingYearIsClosed.text')}</p>}
      <Button disabled={!openReportingYears.includes(year)} onClick={() => setEditMode(!editMode)}>
        {editMode
          ? tSiva('ideasAndCompanies.exitEditing.buttonText')
          : tSiva('ideasAndCompanies.editResults.buttonText')}
      </Button>

      <div className="max-inline-size--50">
        {sortedData?.map((category) => (
          <div key={category.goalCategory.code}>
            <h3>{category.goalCategory.name}</h3>
            {category.targetNumbers.map((targetNumber) => (
              <ValueField
                label={t(targetNumber.goalDetailDto.code as keyof (typeof resources)['nb']['targetNumbers'])}
                key={targetNumber.goalTemplateId}
                value={renderInput(targetNumber)}
              />
            ))}
          </div>
        ))}
      </div>
    </>
  );
};

export default GoalAchievementYear;
